import {
    allowNextNavigation,
    api,
    ApiError,
    apiErrorMessage,
    formatDate,
    useZodForm,
} from '@bemor/web-kit';
import {
    DateTime,
    Field,
    FormSheet,
    Input,
    Money,
    MoneyInput,
    SearchableSelect,
    type SelectOption,
} from '@bemor/web-kit/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import type { ChargebackRow, Dispute } from '@/api/types';
import { useWrite } from '@/lib/queries';

/** What a dispute open touches: the lists, the queues (the alert closes), the cases (the dispute case opens). */
const INVALIDATES = [
    '/staff/disputes',
    '/staff/queues',
    '/staff/cases',
    '/staff/charges',
];

const schema = z.object({
    charge_id: z.string().trim().min(1, 'Enter the charge id.'),
    amount: z.number().int().positive().nullable(),
    reason_code: z.string().trim().max(16, 'At most 16 characters.'),
    provider_case_id: z.string().trim().max(64, 'At most 64 characters.'),
});

/**
 * BM-127: staff open a chargeback that reached Bemor outside NMI's webhook (a Netevia report). Every dispute is a
 * chargeback (decision 1); the case id is the processor's, and a second dispute with it is refused (409).
 */
export function OpenDisputeSheet({ onClose }: { onClose: () => void }) {
    const navigate = useNavigate();
    const [key] = useState(() => crypto.randomUUID());
    const [error, setError] = useState<unknown>(null);
    const form = useZodForm(schema, {
        defaultValues: {
            charge_id: '',
            amount: null,
            reason_code: '',
            provider_case_id: '',
        },
    });
    const open = useWrite(
        async (body: Record<string, unknown>) =>
            (
                await api().post<Dispute>('/staff/disputes', body, {
                    idempotencyKey: key,
                })
            ).data,
        INVALIDATES,
    );
    return (
        <FormSheet
            open
            onOpenChange={(o) => (o ? null : onClose())}
            size="sm"
            title="Open a dispute"
            submitLabel="Open dispute"
            busy={open.isPending}
            dirty={form.formState.isDirty}
            error={error}
            data-testid="open-dispute-form"
            onSubmit={form.handleSubmit((v) => {
                setError(null);
                open.mutate(
                    {
                        charge_id: v.charge_id,
                        ...(v.amount ? { amount_cents: v.amount } : {}),
                        ...(v.reason_code
                            ? { reason_code: v.reason_code }
                            : {}),
                        ...(v.provider_case_id
                            ? { provider_case_id: v.provider_case_id }
                            : {}),
                    },
                    {
                        onSuccess: (d) => {
                            // QA D2: saved; the guard of the dirty sheet would hold the navigation (Cases' pattern)
                            form.reset();
                            onClose();
                            allowNextNavigation();
                            navigate(`/disputes/${d.id}`);
                        },
                        onError: (e) => {
                            if (
                                ApiError.is(e) &&
                                (e.key === 'dispute_exists' ||
                                    e.errors[0]?.field === 'provider_case_id')
                            ) {
                                form.setError('provider_case_id', {
                                    message:
                                        e.key === 'dispute_exists'
                                            ? 'A dispute with this case id exists already.'
                                            : apiErrorMessage(e),
                                });
                                return;
                            }
                            setError(e);
                        },
                    },
                );
            })}
        >
            <Field
                id="charge_id"
                label="Charge"
                error={form.formState.errors.charge_id?.message}
            >
                <Input
                    id="charge_id"
                    placeholder="chg_…"
                    {...form.register('charge_id')}
                />
            </Field>
            <Field
                id="amount"
                label="Amount (USD), empty for the full charge"
                error={form.formState.errors.amount?.message}
            >
                <MoneyInput
                    id="amount"
                    cents={form.watch('amount')}
                    onChange={(cents) =>
                        form.setValue('amount', cents ?? null, {
                            shouldDirty: true,
                        })
                    }
                />
            </Field>
            <Field
                id="reason_code"
                label="Reason code"
                error={form.formState.errors.reason_code?.message}
            >
                <Input
                    id="reason_code"
                    placeholder="10.4"
                    {...form.register('reason_code')}
                />
            </Field>
            <Field
                id="provider_case_id"
                label="Case id (optional)"
                error={form.formState.errors.provider_case_id?.message}
            >
                <Input
                    id="provider_case_id"
                    placeholder="Netevia's or NMI's chargeback id"
                    {...form.register('provider_case_id')}
                />
            </Field>
        </FormSheet>
    );
}

/** The candidates of a row as options: the charge, its amount and capture day. */
export function candidateOptions(row: ChargebackRow): SelectOption[] {
    return row.candidates.map((c) => ({
        value: c.charge_id,
        label: c.charge_id,
        // review: the capture day in the viewer's zone ("Oct 8, 2026"), never the raw UTC ISO date (UI rule 0.2)
        description: `${c.merchant_id}${c.captured_at ? ` · captured ${formatDate(c.captured_at)}` : ''}`,
    }));
}

/**
 * BM-127: an unmatched chargeback row (no captured charge, or several, matched its card, amount and date); staff pick
 * the charge, from the candidates or by id, and the dispute opens with the row's amount, reason and case id.
 */
export function OpenFromRowSheet({
    row,
    onClose,
}: {
    row: ChargebackRow;
    onClose: () => void;
}) {
    const navigate = useNavigate();
    const [key] = useState(() => crypto.randomUUID());
    const initialPick =
        row.candidates.length === 1 ? row.candidates[0]!.charge_id : null;
    const [picked, setPicked] = useState<string | null>(initialPick);
    const [typed, setTyped] = useState('');
    const [error, setError] = useState<unknown>(null);
    const chargeId = typed.trim() !== '' ? typed.trim() : picked;
    const open = useWrite(
        async (charge: string) =>
            (
                await api().post<Dispute>(
                    `/staff/disputes/rows/${row.id}/open`,
                    { charge_id: charge },
                    { idempotencyKey: key },
                )
            ).data,
        INVALIDATES,
    );
    return (
        <FormSheet
            open
            onOpenChange={(o) => (o ? null : onClose())}
            size="sm"
            title="Open dispute"
            submitLabel="Open dispute"
            busy={open.isPending}
            submitDisabled={!chargeId}
            // review: the pre-picked single candidate is not a change; only staff's own choice or typing is
            dirty={typed.trim() !== '' || picked !== initialPick}
            error={error}
            data-testid="open-from-row-form"
            onSubmit={(e) => {
                e.preventDefault();
                if (!chargeId) return;
                setError(null);
                open.mutate(chargeId, {
                    onSuccess: (d) => {
                        // QA D2: the row is matched; close the sheet and let the navigation past its dirty guard
                        onClose();
                        allowNextNavigation();
                        navigate(`/disputes/${d.id}`);
                    },
                    onError: setError,
                });
            }}
        >
            <dl
                className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2"
                data-testid="chargeback-row"
            >
                <div>
                    <dt className="text-muted-foreground">Amount</dt>
                    <dd>
                        <Money cents={row.amount_cents} />
                    </dd>
                </div>
                <div>
                    <dt className="text-muted-foreground">Card</dt>
                    <dd className="font-mono text-xs">
                        {row.card_bin ?? '······'}…{row.card_last4 ?? '····'}
                    </dd>
                </div>
                <div>
                    <dt className="text-muted-foreground">Chargeback date</dt>
                    <dd>
                        <DateTime value={row.occurred_on} format="date" />
                    </dd>
                </div>
                <div>
                    <dt className="text-muted-foreground">NMI chargeback</dt>
                    <dd className="font-mono text-xs">
                        {row.provider_case_id}
                    </dd>
                </div>
                <div className="md:col-span-2">
                    <dt className="text-muted-foreground">Reason</dt>
                    <dd>{row.reason ?? '—'}</dd>
                </div>
                {row.customer_name ? (
                    <div className="md:col-span-2">
                        <dt className="text-muted-foreground">Cardholder</dt>
                        <dd>{row.customer_name}</dd>
                    </div>
                ) : null}
            </dl>
            {row.candidates.length > 0 ? (
                <SearchableSelect
                    label={`Matching charges (${row.candidates.length})`}
                    options={candidateOptions(row)}
                    value={picked}
                    onChange={setPicked}
                    placeholder="Choose a charge"
                />
            ) : (
                <p className="text-muted-foreground m-0 text-sm">
                    No captured charge matched the card, amount and date.
                </p>
            )}
            <Field id="other_charge" label="Or another charge id">
                <Input
                    id="other_charge"
                    placeholder="chg_…"
                    value={typed}
                    onChange={(e) => setTyped(e.target.value)}
                />
            </Field>
        </FormSheet>
    );
}
